import { useEffect, useState } from 'react';
import { Plus, X, RotateCcw, Save } from 'lucide-react';
import {
  CLAVE_NOTAS_RAPIDAS,
  LIMITE_GRUPOS,
  LIMITE_OPCIONES_POR_GRUPO,
  LIMITE_TEXTO_OPCION,
  NOTAS_RAPIDAS_DEFAULT,
  cabeLaLista,
  leerNotasRapidas,
  limpiarTextoNota,
  serializarNotasRapidas,
  type GrupoNotasRapidas,
} from '@shared/notasRapidas';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 138 — Cómo se pide la carne, en palabras del negocio.
 *
 * La lista nace con lo que el negocio dijo por WhatsApp —términos, cortes, preparación— pero la
 * termina quien está en el mostrador: ya llevan meses operando y saben qué les piden de verdad.
 * Un catálogo cerrado obligaría a pedir versión nueva cada vez que aparezca una forma de pedir un
 * corte, y eso pasa todas las semanas en este ramo.
 *
 * Lo que aquí se escribe es lo que aparece como botón al capturar un producto **y** lo que ve el
 * cliente en el menú en línea: un solo vocabulario para la cocina.
 */
export function NotasRapidasForm() {
  const [grupos, setGrupos] = useState<GrupoNotasRapidas[]>([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const res = await window.api.config.obtener(CLAVE_NOTAS_RAPIDAS);
      setGrupos(leerNotasRapidas(res.ok ? res.data : null));
      setCargando(false);
    })();
  }, []);

  const guardar = async () => {
    setGuardando(true);
    setMensaje(null);
    const res = await window.api.config.actualizar(
      CLAVE_NOTAS_RAPIDAS,
      serializarNotasRapidas(grupos),
    );
    setGuardando(false);
    /*
      Sprint 139 — El mensaje dice lo que de verdad pasa.

      Decía *"los botones ya cambiaron en el punto de venta"*, y no era cierto para ninguna
      pantalla que ya estuviera abierta: cada dispositivo lee la lista al entrar al POS. Una
      pantalla que promete un efecto que no ocurre enseña a desconfiar de todos los mensajes.
    */
    if (res.ok) {
      setMensaje('Guardado. En las cajas que ya están abiertas se ve al volver a entrar al POS.');
    }
    else setMensaje(traducirErrorBackend(res.error, res.codigo));
  };

  const editarGrupo = (i: number, cambio: Partial<GrupoNotasRapidas>) =>
    setGrupos((prev) => prev.map((g, idx) => (idx === i ? { ...g, ...cambio } : g)));

  const quitarOpcion = (i: number, j: number) =>
    editarGrupo(i, { opciones: grupos[i].opciones.filter((_, idx) => idx !== j) });

  const agregarOpcion = (i: number, texto: string) => {
    const limpio = limpiarTextoNota(texto, LIMITE_TEXTO_OPCION);
    if (!limpio) return;
    if (grupos[i].opciones.includes(limpio)) return;
    if (grupos[i].opciones.length >= LIMITE_OPCIONES_POR_GRUPO) {
      setMensaje(`Este apartado ya llegó a ${LIMITE_OPCIONES_POR_GRUPO} opciones. Quita alguna que no uses.`);
      return;
    }
    /*
      El aviso va ANTES de guardar y en palabras del negocio. Si se dejara para el servidor, la
      respuesta sería "Valor de configuración inválido" con la captura entera perdida.
    */
    const propuesta = grupos.map((g, idx) =>
      idx === i ? { ...g, opciones: [...g.opciones, limpio] } : g,
    );
    if (!cabeLaLista(propuesta)) {
      setMensaje('Ya no caben más opciones. Quita alguna que no uses y vuelve a intentar.');
      return;
    }
    setMensaje(null);
    editarGrupo(i, { opciones: [...grupos[i].opciones, limpio] });
  };

  if (cargando) return <div className="text-slate-500">Cargando…</div>;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Cómo se pide la carne</h2>
        <p className="text-sm text-slate-600">
          Estos botones aparecen al capturar un producto, para anotar cómo lo quiere el cliente.
          También los ve quien pide desde el menú en línea. Escribe las palabras que usan aquí —son
          las que va a leer la cocina.
        </p>
      </div>

      {grupos.map((g, i) => (
        <div key={i} className="rounded-xl border border-slate-200 p-3">
          <div className="flex items-center gap-2">
            <input
              className="input flex-1 font-semibold"
              value={g.titulo}
              maxLength={LIMITE_TEXTO_OPCION}
              onChange={(e) => editarGrupo(i, { titulo: e.target.value })}
              aria-label="Nombre del apartado"
            />
            <button
              type="button"
              onClick={() => setGrupos((prev) => prev.filter((_, idx) => idx !== i))}
              className="btn-ghost min-h-[44px] px-3 text-red-600"
              aria-label={`Quitar el apartado ${g.titulo}`}
            >
              Quitar apartado
            </button>
          </div>

          <div className="mt-2 flex flex-wrap gap-2">
            {g.opciones.map((o, j) => (
              <span
                key={j}
                className="flex min-h-[40px] items-center gap-1 rounded-lg border border-slate-300 bg-white pl-3 text-base"
              >
                {o}
                <button
                  type="button"
                  onClick={() => quitarOpcion(i, j)}
                  className="flex h-10 w-10 items-center justify-center text-slate-500 hover:text-red-600"
                  aria-label={`Quitar ${o}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </span>
            ))}
            {g.opciones.length < LIMITE_OPCIONES_POR_GRUPO && (
              <AgregarOpcion onAgregar={(t) => agregarOpcion(i, t)} />
            )}
          </div>
        </div>
      ))}

      <div className="flex flex-wrap gap-2">
        {grupos.length < LIMITE_GRUPOS && (
          <button
            type="button"
            onClick={() =>
              setGrupos((prev) => [...prev, { titulo: 'Nuevo apartado', opciones: [] }])
            }
            className="btn-ghost min-h-[44px] gap-1"
          >
            <Plus className="h-4 w-4" /> Agregar apartado
          </button>
        )}
        <button
          type="button"
          onClick={() => setGrupos(NOTAS_RAPIDAS_DEFAULT)}
          className="btn-ghost min-h-[44px] gap-1"
        >
          <RotateCcw className="h-4 w-4" /> Volver a la lista original
        </button>
        <button
          type="button"
          onClick={() => void guardar()}
          disabled={guardando}
          className="btn-primary min-h-[44px] gap-1"
        >
          <Save className="h-4 w-4" /> {guardando ? 'Guardando…' : 'Guardar'}
        </button>
      </div>

      {mensaje && <p className="text-sm font-medium text-slate-800">{mensaje}</p>}

      {/*
        Un apartado sin opciones no se guarda: se descarta al serializar. Decirlo aquí evita que
        alguien cree "Salsas", no escriba nada y crea que el sistema perdió su trabajo.
      */}
      <p className="text-xs text-slate-500">
        Un apartado sin opciones no se guarda. Si quitas todos, al capturar sólo quedará el espacio
        para escribir a mano.
      </p>
    </div>
  );
}

function AgregarOpcion({ onAgregar }: { onAgregar: (texto: string) => void }) {
  const [texto, setTexto] = useState('');
  const confirmar = () => {
    onAgregar(texto);
    setTexto('');
  };
  return (
    <span className="flex items-center gap-1">
      <input
        className="input h-10 w-40"
        value={texto}
        maxLength={LIMITE_TEXTO_OPCION}
        placeholder="Ej: Bien dorada"
        onChange={(e) => setTexto(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            confirmar();
          }
        }}
      />
      <button
        type="button"
        onClick={confirmar}
        className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-300"
        aria-label="Agregar opción"
      >
        <Plus className="h-4 w-4" />
      </button>
    </span>
  );
}
